import type { Emote, EmoteCategory } from '@/types/social'

// ============================================
// Emote Catalog
// ============================================

export const EMOTE_CATALOG: Emote[] = [
  // ==========================================
  // Affection - Sweet & loving
  // ==========================================
  {
    id: 'headpat',
    name: 'Head Pat',
    emoji: '🥰',
    category: 'affection',
    isSpicy: false,
    notificationText: 'gave you a head pat~'
  },
  {
    id: 'hug',
    name: 'Hug',
    emoji: '🤗',
    category: 'affection',
    isSpicy: false,
    notificationText: 'wants to hug you!'
  },
  {
    id: 'kiss',
    name: 'Kiss',
    emoji: '😘',
    category: 'affection',
    isSpicy: false,
    notificationText: 'sent you a kiss~'
  },
  {
    id: 'cuddle',
    name: 'Cuddle',
    emoji: '🥺',
    category: 'affection',
    isSpicy: false,
    notificationText: 'wants to cuddle!'
  },
  {
    id: 'hold_hands',
    name: 'Hold Hands',
    emoji: '🫂',
    category: 'affection',
    isSpicy: false,
    notificationText: 'wants to hold your hand'
  },
  {
    id: 'miss_you',
    name: 'Miss You',
    emoji: '💕',
    category: 'affection',
    isSpicy: false,
    notificationText: 'misses you!'
  },

  // ==========================================
  // Motivation - Encouraging & supportive
  // ==========================================
  {
    id: 'cheer',
    name: 'Cheer',
    emoji: '📣',
    category: 'motivation',
    isSpicy: false,
    notificationText: 'is cheering you on!'
  },
  {
    id: 'praise',
    name: 'Praise',
    emoji: '⭐',
    category: 'motivation',
    isSpicy: false,
    notificationText: 'thinks you\'re amazing!'
  },
  {
    id: 'believe',
    name: 'I Believe In You',
    emoji: '💪',
    category: 'motivation',
    isSpicy: false,
    notificationText: 'believes in you!'
  },
  {
    id: 'celebrate',
    name: 'Celebrate',
    emoji: '🎉',
    category: 'motivation',
    isSpicy: false,
    notificationText: 'is celebrating with you!'
  },
  {
    id: 'goodjob',
    name: 'Good Job',
    emoji: '👏',
    category: 'motivation',
    isSpicy: false,
    notificationText: 'says good job!'
  },
  {
    id: 'proud',
    name: 'So Proud',
    emoji: '🌟',
    category: 'motivation',
    isSpicy: false,
    notificationText: 'is so proud of you!'
  },

  // ==========================================
  // Playful - Teasing & fun
  // ==========================================
  {
    id: 'poke',
    name: 'Poke',
    emoji: '👉',
    category: 'playful',
    isSpicy: false,
    notificationText: 'poked you!'
  },
  {
    id: 'pout',
    name: 'Pout',
    emoji: '😤',
    category: 'playful',
    isSpicy: false,
    notificationText: 'is pouting at you!'
  },
  {
    id: 'tease',
    name: 'Tease',
    emoji: '😏',
    category: 'playful',
    isSpicy: false,
    notificationText: 'is teasing you~'
  },
  {
    id: 'shy',
    name: 'Shy',
    emoji: '👉👈',
    category: 'playful',
    isSpicy: false,
    notificationText: 'is being shy...'
  },
  {
    id: 'bonk',
    name: 'Bonk',
    emoji: '🔨',
    category: 'playful',
    isSpicy: false,
    notificationText: 'bonked you!'
  },
  {
    id: 'blush',
    name: 'Blush',
    emoji: '😊',
    category: 'playful',
    isSpicy: false,
    notificationText: 'is blushing because of you~'
  },

  // ==========================================
  // Spicy - Requires mutual spicy mode
  // ==========================================
  {
    id: 'wink',
    name: 'Flirty Wink',
    emoji: '😉',
    category: 'spicy',
    isSpicy: true,
    notificationText: 'sent you a flirty wink~'
  },
  {
    id: 'bite_lip',
    name: 'Bite Lip',
    emoji: '😬',
    category: 'spicy',
    isSpicy: true,
    notificationText: 'is biting their lip...'
  },
  {
    id: 'hot',
    name: 'You\'re Hot',
    emoji: '🔥',
    category: 'spicy',
    isSpicy: true,
    notificationText: 'thinks you\'re hot!'
  },
  {
    id: 'come_here',
    name: 'Come Here',
    emoji: '🫦',
    category: 'spicy',
    isSpicy: true,
    notificationText: 'wants you to come closer...'
  },
  {
    id: 'thinking_of_you',
    name: 'Thinking of You',
    emoji: '💭',
    category: 'spicy',
    isSpicy: true,
    notificationText: 'can\'t stop thinking about you~'
  },
  {
    id: 'want_you',
    name: 'Want You',
    emoji: '❤️‍🔥',
    category: 'spicy',
    isSpicy: true,
    notificationText: 'wants you...'
  },
]

// ============================================
// Helper functions
// ============================================

export function getEmoteById(id: string): Emote | undefined {
  return EMOTE_CATALOG.find(e => e.id === id)
}

export function getEmotesByCategory(category: EmoteCategory): Emote[] {
  return EMOTE_CATALOG.filter(e => e.category === category)
}

export function getSafeEmotes(): Emote[] {
  return EMOTE_CATALOG.filter(e => !e.isSpicy)
}

export function getSpicyEmotes(): Emote[] {
  return EMOTE_CATALOG.filter(e => e.isSpicy)
}

export const EMOTE_CATEGORIES: { id: EmoteCategory; label: string; icon: string }[] = [
  { id: 'affection', label: 'Affection', icon: '💕' },
  { id: 'motivation', label: 'Motivation', icon: '💪' },
  { id: 'playful', label: 'Playful', icon: '😜' },
  { id: 'spicy', label: 'Spicy', icon: '🔥' },
]

// Format notification message
export function formatEmoteNotification(
  emote: Emote,
  senderName: string
): { title: string; body: string } {
  return {
    title: `${emote.emoji} ${senderName}`,
    body: `${senderName} ${emote.notificationText}`
  }
}
